// BRK-S169 — two configured fields, one control, a mode toggle between them.
//
// ── THE SHAPE, AND WHY IT IS DECLARED HERE ──────────────────────────────────
//
// The surety scoping document asks for "Bond penalty: a toggle between % and $,
// then the amount". The configuration already declares BOTH numbers — a
// currency `bondAmount` labelled "Bond penalty" (the penal sum's proper name)
// and a `bidBondPenaltyPercent` — because on a bid bond the penal sum is
// normally stated as a percentage of the bid and occasionally as a figure. Two
// boxes side by side invite a broker to fill in both, and a penal sum stated
// twice is a penal sum nobody can rely on.
//
// So one control, and the mode says which of the two it is writing. The other
// is CLEARED, which is the whole point: exactly one of them ever reaches the
// filing.
//
// ⚠️ **CONTRACT GAP, FILED (Aaron, decision 9, 2026-09-21).** The right place
// for "these two fields are one question" is the configuration that declares
// them — a `pairedWith` hint, closed-mirrored like every other field
// attribute. It is NOT in the contract for wave 1, so this table stands in, and
// it is the ONLY place in this client that names either field. When the hint
// lands, this table is deleted and `resolveBondPairs` reads the configuration
// instead; nothing else changes, because nothing else knows the names.
// See docs/bonds.md § The bond penalty control.
//
// ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
//
// No subproduct, no product, no bond type. "Default to % on a bid bond" is
// expressed as "default to the percent mode when the configuration declares
// BOTH members" — because a configuration that declares a bid-bond percentage
// IS the bid-bond configuration, and a client that branched on the subproduct
// would be a second, worse copy of that fact.
import type { BondRenderField } from "@/lib/bondFields";
import { isAnswered, type BondValueBag } from "@/lib/bondFields";

/** One side of a paired control: which field it writes, and its unit. */
export interface BondPairMode {
  /** Stable id for the toggle's state and its tests. */
  id: string;
  /** The configuration key this mode writes. */
  key: string;
  /** What the toggle button says. A unit, not a word: the control carries one
   *  label, and the toggle says which unit that one number is in. */
  symbol: string;
  /** The accessible name for the toggle button, since "%" and "$" read as
   *  punctuation to a screen reader. */
  description: string;
}

export interface BondFieldPair {
  /** Stable id for the composite — used as its React key and in tests. */
  id: string;
  /** The ONE label the composite carries, whichever mode is active. Taken from
   *  this client rather than from either field's own label, because the two
   *  configured labels disagree ("Bond penalty" vs "Bid bond penalty %") and
   *  the second names a unit the toggle is already showing. */
  label: string;
  /** Exactly two, in the order the toggle offers them. */
  modes: readonly [BondPairMode, BondPairMode];
  /** Which mode a composite with nothing filled in starts on. */
  defaultModeId: string;
  // No `help` (BRK-S181): the composite's hint ("Enter the penal sum either as
  // a percentage of the bid or as a dollar amount…") was removed, because the
  // inline toggle already says which unit the one number is in.
}

export const BOND_FIELD_PAIRS: readonly BondFieldPair[] = [
  {
    id: "bondPenalty",
    label: "Bond penalty",
    modes: [
      {
        id: "percent",
        key: "bidBondPenaltyPercent",
        symbol: "%",
        description: "Percent of the bid",
      },
      {
        id: "amount",
        key: "bondAmount",
        symbol: "$",
        description: "A dollar amount",
      },
    ],
    // On a bid bond the penal sum is a percentage of the bid, so that is where
    // a broker starts. See the note above for why this is not a subproduct
    // branch.
    defaultModeId: "percent",
  },
];

/** One thing the terms step draws: an ordinary field, or a paired composite. */
export type BondRenderUnit =
  | { kind: "field"; id: string; field: BondRenderField }
  | {
      kind: "pair";
      id: string;
      pair: BondFieldPair;
      /** The configured field behind each mode, in `pair.modes` order. Both are
       *  present — a pair with a missing member is never formed. */
      fields: readonly [BondRenderField, BondRenderField];
    };

/**
 * The step's fields, with any declared pair collapsed into one unit.
 *
 * **A pair forms only when BOTH members are declared.** A configuration
 * carrying just one of them has asked one question, and drawing a mode toggle
 * whose other half writes a field nobody declared would offer the broker a way
 * to put a value somewhere the server will not look. So the single member
 * renders as an ordinary field, exactly as it does today.
 *
 * The composite takes the position of the FIRST member to appear, and the
 * second is removed. Order is otherwise untouched: the configuration decides
 * where its questions sit, and hoisting a composite to the top would reorder a
 * step the service laid out.
 */
export function resolveBondPairs(
  fields: readonly BondRenderField[],
): BondRenderUnit[] {
  const byName = new Map(fields.map((field) => [field.name, field]));
  const claimed = new Map<string, BondFieldPair>();
  for (const pair of BOND_FIELD_PAIRS) {
    if (pair.modes.every((mode) => byName.has(mode.key))) {
      for (const mode of pair.modes) claimed.set(mode.key, pair);
    }
  }
  const emitted = new Set<string>();
  const units: BondRenderUnit[] = [];
  for (const field of fields) {
    const pair = claimed.get(field.name);
    if (pair === undefined) {
      units.push({ kind: "field", id: field.name, field });
      continue;
    }
    if (emitted.has(pair.id)) continue;
    emitted.add(pair.id);
    const [first, second] = pair.modes;
    units.push({
      kind: "pair",
      id: pair.id,
      pair,
      fields: [
        byName.get(first.key) as BondRenderField,
        byName.get(second.key) as BondRenderField,
      ],
    });
  }
  return units;
}

/**
 * Which mode a composite should be showing, given what is already captured.
 *
 * **RESUME READS THE VALUES, not a remembered choice.** There is no client-side
 * persistence in this flow (NN #9) — a reload rebuilds everything from the
 * order — so "which mode was the broker in" can only be answered by which field
 * holds a number. An order carrying a percentage opens on %, one carrying an
 * amount opens on $, and one carrying neither opens on the declared default.
 *
 * Both populated is not a state this control can produce (switching modes
 * clears the other), but a bag from before this story, or from the admin side,
 * can hold both. The FIRST mode in the pair's own order wins, deterministically,
 * rather than whichever happens to be read first.
 */
export function activeBondPairMode(
  unit: Extract<BondRenderUnit, { kind: "pair" }>,
  values: BondValueBag,
): BondPairMode {
  const { pair, fields } = unit;
  for (const [index, mode] of pair.modes.entries()) {
    if (isAnswered(fields[index], values[mode.key])) return mode;
  }
  return (
    pair.modes.find((mode) => mode.id === pair.defaultModeId) ?? pair.modes[0]
  );
}

/**
 * The field names a paired sibling has already answered on the step's behalf.
 *
 * The required check asks each field individually, and a composite deliberately
 * leaves one of its two members empty — so a configuration that marks either
 * one required would block a step the broker has in fact answered. Anything in
 * this set is exempt from the unanswered checklist.
 *
 * It is a SET of names rather than a filtered field list so the caller keeps
 * its own ordering and its own list; the rule here is only "who is covered".
 */
export function bondPairSatisfied(
  fields: readonly BondRenderField[],
  values: BondValueBag,
): Set<string> {
  const covered = new Set<string>();
  for (const unit of resolveBondPairs(fields)) {
    if (unit.kind !== "pair") continue;
    const answered = unit.pair.modes.some((mode, index) =>
      isAnswered(unit.fields[index], values[mode.key]),
    );
    if (answered) for (const mode of unit.pair.modes) covered.add(mode.key);
  }
  return covered;
}
